/**
 * Map builder — initializes a MapLibre GL JS map with self-hosted vector tiles.
 */

import { DEFAULTS } from './config.js';
import { createMapStyle } from './map-style.js';

/**
 * Create and return a MapLibre map instance.
 *
 * @param {HTMLElement} el — Container element
 * @param {Object} config — Parsed config
 * @param {Array} locations — Location data (used for auto-bounds)
 * @returns {maplibregl.Map}
 */
export function createMap(el, config, locations) {
  const maplibregl = window.maplibregl;

  const style = createMapStyle(config.tileServer);

  const mapOptions = {
    container: el,
    style: style,
    minZoom: config.minZoom,
    maxZoom: config.maxZoom,
    attributionControl: false,
    scrollZoom: config.scrollZoom,
  };

  // Set initial view: explicit center+zoom, or auto-fit to markers
  if (config.center && config.zoom) {
    // config.center is [lat, lng] from the data attribute — MapLibre needs [lng, lat]
    mapOptions.center = [config.center[1], config.center[0]];
    mapOptions.zoom = config.zoom;
  }

  const map = new maplibregl.Map(mapOptions);

  // Add navigation control (zoom buttons) — top right
  map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');

  // Add attribution
  map.addControl(new maplibregl.AttributionControl({
    compact: true,
    customAttribution: DEFAULTS.attribution,
  }));

  // Auto-fit bounds if no explicit center/zoom
  if (!config.center || !config.zoom) {
    const bounds = new maplibregl.LngLatBounds();
    locations.forEach(loc => bounds.extend([loc.lng, loc.lat]));
    map.fitBounds(bounds, { padding: 40, maxZoom: 15 });
  }

  return map;
}
